import { Box, Typography } from '@mui/material';

import { PLAN_COLORS } from '../constants';
import type { PlanConfig, PlanSummary } from '../types';
import { METRIC_GRID_COMPACT_HEIGHT } from '../utils/metricGridLayout';

interface MetricGridBaseProps {
  summary: PlanSummary;
  config: PlanConfig;
}

type Accent = 'orange' | 'purple' | undefined;

interface MetricCard {
  label: string;
  shortLabel: string;
  value: string | number;
  sub: string;
  accent?: Accent;
}

const accentColor = (accent: Accent) =>
  accent === 'orange' ? PLAN_COLORS.orange : accent === 'purple' ? PLAN_COLORS.both : undefined;

function buildMetricCards(summary: PlanSummary, config: PlanConfig): MetricCard[] {
  const dailyBatch = summary.dailyBatch ?? summary.lprb;
  const peakDemand = summary.peakDemand ?? summary.emailsSteady;

  return [
    {
      label: 'Total addressable leads',
      shortLabel: 'Leads',
      value: summary.totalLeads.toLocaleString(),
      sub: 'valid email + opted-in',
      accent: 'orange',
    },
    {
      label: 'New leads start day',
      shortLabel: 'New day',
      value: `Day ${summary.newStartDay}`,
      sub: summary.newStartDay <= summary.rampDays ? 'during ramp-up' : 'after ramp-up ends',
      accent: 'purple',
    },
    {
      label: 'Daily onboarding batch',
      shortLabel: 'Ramp batch',
      value: dailyBatch.toLocaleString(),
      sub: `leads/day during ramp (days 1–${summary.rampDays})`,
      accent: 'orange',
    },
    {
      label: 'Peak email demand',
      shortLabel: 'Peak',
      value: peakDemand.toLocaleString(),
      sub: 'onboarding + follow-ups at ramp end',
    },
    {
      label: 'Steady-state demand',
      shortLabel: 'Steady',
      value: (summary.steadyDemand ?? summary.emailsSteady).toLocaleString(),
      sub: `emails/day at ${config.freqS}d frequency`,
    },
    {
      label: 'Addresses needed',
      shortLabel: 'Addrs',
      value: summary.addrsSteady,
      sub: 'at 80% usable capacity',
    },
    {
      label: 'Leads / steady batch',
      shortLabel: 'SS batch',
      value: summary.ssLpb.toLocaleString(),
      sub: `every ${summary.ssCad}d after ramp`,
    },
    {
      label: `Capacity at day ${Math.floor(summary.rampDays / 2)}`,
      shortLabel: `Day ${Math.floor(summary.rampDays / 2)}`,
      value: summary.midCap.toLocaleString(),
      sub: 'emails/day (mid-ramp)',
    },
  ];
}

/** Full 2×4 grid in document flow — scrolls away naturally. */
export const ExpandedMetricGrid = ({ summary, config }: MetricGridBaseProps) => {
  const cards = buildMetricCards(summary, config);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        gap: 1.5,
        mb: 3,
      }}
    >
      {cards.map((c) => {
        const color = accentColor(c.accent);
        return (
          <Box
            key={c.label}
            title={c.label}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: color ?? 'divider',
              borderLeftWidth: color ? 3 : 1,
              borderRadius: 1.5,
              p: 1.75,
              boxShadow: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                color: 'text.secondary',
                mb: 0.5,
                fontWeight: 500,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {c.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: 'monospace',
                lineHeight: 1.1,
                color: color ?? 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {c.value}
            </Typography>
            <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.5 }}>{c.sub}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/** Single-row compact metrics — overlaid above sticky tabs via CSS var opacity. */
export const CompactMetricRow = ({ summary, config }: MetricGridBaseProps) => {
  const cards = buildMetricCards(summary, config);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
        gap: 0.375,
        height: METRIC_GRID_COMPACT_HEIGHT,
        alignItems: 'center',
      }}
    >
      {cards.map((c) => {
        const color = accentColor(c.accent);
        return (
          <Box
            key={c.label}
            title={c.label}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: color ?? 'divider',
              borderLeftWidth: color ? 2 : 1,
              borderRadius: 1,
              px: 0.5,
              py: 0.375,
              minWidth: 0,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: 8,
                color: 'text.secondary',
                fontWeight: 500,
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {c.shortLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'monospace',
                lineHeight: 1.1,
                color: color ?? 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {c.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
